import React, { useState, useEffect, useContext, createContext } from 'react'
import { useNavigate } from 'react-router-dom'

const AUTH_STORAGE_KEY = 'spoeum-auth'

/**
 * Bearer 헤더용 액세스 토큰.
 * 1) spoeum-auth JSON의 token
 * 2) 레거시 localStorage `token` (JSON 문자열이면 파싱, 아니면 원문 JWT)
 */
export const getAccessTokenFromStorage = () => {
	try {
		const raw = localStorage.getItem(AUTH_STORAGE_KEY)
		if (raw) {
			const { token } = JSON.parse(raw)
			if (token != null && token !== '') return String(token)
		}
	} catch {
		// ignore
	}
	const legacy = localStorage.getItem('token')
	if (legacy == null || legacy === '') return null
	try {
		const parsed = JSON.parse(legacy)
		return typeof parsed === 'string' ? parsed : String(parsed)
	} catch {
		return legacy
	}
}

const AuthStateContext = createContext(undefined)

export const AuthProvider = ({ children }) => {
	const navigate = useNavigate()
	const [authState, setAuthState] = useState({
		state: 'loading',
	})

	useEffect(() => {
		// localStorage에서 인증 정보 읽기
		const storedAuth = localStorage.getItem(AUTH_STORAGE_KEY)

		if (storedAuth) {
			try {
				const { token, user } = JSON.parse(storedAuth)
				if (token && user) {
					setAuthState({
						state: 'loaded',
						isAuthentication: true,
						token,
						user,
					})
					return
				}
			} catch (error) {
				console.error('Failed to parse auth data:', error)
			}
		}

		setAuthState({ state: 'loaded', isAuthentication: false })
	}, [])

	// 로그인 시 token과 기본 user 정보 저장
	const setAuth = (token, user) => {
		const authData = { token, user }
		localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData))
		setAuthState({
			state: 'loaded',
			isAuthentication: true,
			token,
			user,
		})
	}

	const handleLogout = () => {
		// localStorage에서 인증 정보 제거
		localStorage.removeItem(AUTH_STORAGE_KEY)

		// 내부 auth 컨텍스트 상태도 "로그아웃"으로 변경
		setAuthState({ state: 'loaded', isAuthentication: false })

		// 로그인 화면으로 리다이렉트
		navigate('/login')
	}

	// axiosInstance에서 발생시킨 로그아웃 이벤트 처리
	useEffect(() => {
		const handleAuthLogoutEvent = () => {
			console.log('토큰 만료로 인한 자동 로그아웃')
			handleLogout()
		}

		window.addEventListener('auth:logout', handleAuthLogoutEvent)

		return () => {
			window.removeEventListener('auth:logout', handleAuthLogoutEvent)
		}
	}, [])

	return (
		<AuthStateContext.Provider value={[authState, handleLogout, setAuth]}>
			{children}
		</AuthStateContext.Provider>
	)
}

export const useAuthStatus = () => {
	const context = useContext(AuthStateContext)

	if (!context) throw new Error('AuthProvider not found')

	const [authStatus, handleLogout, setAuth] = context
	return [authStatus, handleLogout, setAuth]
}

// 토큰만 간단하게 가져오는 훅
export const useToken = () => {
	const [authStatus] = useAuthStatus()
	// console.log(authStatus)
	return authStatus.token
}

export const SignIn = async (input) => {
	return await fetch(`${process.env.REACT_APP_API_ENDPOINT}/auth/signin`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(input),
	})
		.then(async (response) => {
			const result = await response.json()

			if (result.success === false) throw result

			return result?.user
		})
		.catch((errors) => {
			console.log(errors)
		})
}
